import { type ZCalendarEvent } from '../constants';
import { type Interval, intervalsOverlap, isCancelledTourEvent, isTourEvent } from './tourEventPredicates';

// A slot renders at most this many tour tiles. Availability blocks are the background container,
// not a tile, so they never count toward the cap.
export const MAX_TILES_PER_SLOT = 3;

type EventAccessors = {
  start: (event: ZCalendarEvent) => Date;
  end: (event: ZCalendarEvent) => Date;
};

// Highest number of already-kept tour tiles that overlap any single instant within [start, end).
// The busiest instant can only sit at the interval's own start or at a kept tile's start inside it,
// so those are the only points worth checking.
const maxConcurrent = (kept: Array<Interval>, interval: Interval): number => {
  const overlapping = kept.filter((other) => intervalsOverlap(other, interval));
  const points = [interval.start, ...overlapping.map((other) => other.start)].filter(
    (point) => point >= interval.start && point < interval.end,
  );
  return points.reduce((max, point) => {
    const count = overlapping.filter((other) => other.start <= point && point < other.end).length;
    return Math.max(max, count);
  }, 0);
};

/**
 * Cap the number of tour tiles that render in any single time slot, prioritising active tours over
 * cancelled ones. Active/confirmed tours are never dropped (AC3); only cancelled tours yield when a
 * slot would otherwise show more than `maxPerSlot` tiles. Availability blocks are left untouched and
 * do not count toward the cap. Non-tour and non-overlapping events pass through unchanged.
 */
export const capTourEventsPerSlot = (
  events: Array<ZCalendarEvent>,
  accessors: EventAccessors,
  maxPerSlot: number = MAX_TILES_PER_SLOT,
): Array<ZCalendarEvent> => {
  const cancelled = events.filter(isCancelledTourEvent);
  // Crowding past the cap is driven by cancelled tours; with none present nothing can be dropped.
  if (cancelled.length === 0) return events;

  const toInterval = (event: ZCalendarEvent): Interval => ({
    start: accessors.start(event).getTime(),
    end: accessors.end(event).getTime(),
  });

  // Seed the kept set with every active tour so cancelled tours only ever fill the leftover capacity.
  const keptIntervals = events.filter((event) => isTourEvent(event) && !isCancelledTourEvent(event)).map(toInterval);

  // Consider cancelled tours in start order so the earliest-starting ones win the remaining slots.
  const cancelledByStart = [...cancelled].sort((a, b) => accessors.start(a).getTime() - accessors.start(b).getTime());

  const dropped = new Set<ZCalendarEvent>();
  for (const tour of cancelledByStart) {
    const interval = toInterval(tour);
    if (maxConcurrent(keptIntervals, interval) < maxPerSlot) {
      keptIntervals.push(interval);
    } else {
      dropped.add(tour);
    }
  }

  return dropped.size === 0 ? events : events.filter((event) => !dropped.has(event));
};
